import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert, IconButton, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  Checkbox, ListItemText, Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../services/api.js';
import { formatHebrewDate } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useQuickCreate } from '../../next/quickCreate';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';
import CredentialsDialog from './CredentialsDialog.jsx';

const emptyForm = {
  username: '',
  email: '',
  phone: '',
  roles: [],
  cities: [],
  status: 'active',
};

const UsersManagement = () => {
  useQuickCreate(() => openNewDialog());
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [dialogError, setDialogError] = useState('');

  // Reset password dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUsername, setResetUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, citiesRes] = await Promise.all([
        api.get('/users'),
        api.get('/roles'),
        api.get('/exam-cities?active=1').catch(() => ({ data: { data: [] } })),
      ]);
      setUsers(usersRes.data.data);
      setRoles(rolesRes.data.data);
      const cities = (citiesRes.data?.data || [])
        .map((c) => c.name)
        .filter(Boolean);
      setCityOptions([...new Set(cities)].sort((a, b) => a.localeCompare(b, 'he')));
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Clear messages after 4s
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const openNewDialog = () => {
    setEditUser(null);
    setForm({ ...emptyForm });
    setDialogError('');
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setForm({
      username: user.username,
      email: user.email || '',
      phone: user.phone || '',
      roles: user.roles?.map((r) => r._id) || [],
      cities: Array.isArray(user.cities) ? [...user.cities] : [],
      status: user.status,
    });
    setDialogError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setDialogError('');
    if (!String(form.phone || '').trim()) {
      setDialogError('טלפון נדרש');
      return;
    }
    try {
      if (editUser) {
        // עדכון — לא שולחים סיסמה
        const payload = {
          username: form.username,
          email: form.email,
          phone: form.phone,
          roles: form.roles,
          cities: form.cities,
          status: form.status,
        };
        await api.put(`/users/${editUser._id}`, payload);
        setSuccess('המשתמש עודכן בהצלחה');
      } else {
        // יצירה — השרת יוצר סיסמה אקראית ומחזיר אותה פעם אחת
        const { data } = await api.post('/users', form);
        const { initialPassword, ...created } = data;
        setCredentials({ user: created, password: initialPassword, reason: 'create' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setDialogError(err.response?.data?.error || 'שגיאה');
    }
  };

  const handleDelete = async (user) => {
    if (!(await ckConfirm({ title: 'מחיקת משתמש', message: `למחוק את המשתמש "${user.username}"?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/users/${user._id}`);
      setSuccess('המשתמש נמחק');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה במחיקה');
    }
  };

  const openResetDialog = (user) => {
    setResetUserId(user._id);
    setResetUsername(user.username);
    setNewPassword('');
    setShowResetPassword(false);
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    try {
      // ריק — השרת יוצר סיסמה אקראית
      const { data } = await api.post(`/users/${resetUserId}/reset-password`, { newPassword });
      setResetDialogOpen(false);
      const user = users.find((u) => u._id === resetUserId) || { _id: resetUserId, username: resetUsername };
      setCredentials({ user, password: data.initialPassword || newPassword, reason: 'reset' });
    } catch (err) {
      setDialogError(err.response?.data?.error || 'שגיאה באיפוס סיסמה');
    }
  };

  if (loading) {
    return <Box><Typography>טוען...</Typography></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <AdminPageHeader
        title="ניהול משתמשים"
        sub="משתמשי ממשק הניהול, התפקידים והערים של כל משתמש"
        primary={{ label: 'משתמש חדש', onClick: openNewDialog }}
        legacy={(
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>ניהול משתמשים</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddOutlinedIcon />}
              onClick={openNewDialog}
            >
              משתמש חדש
            </Button>
          </Box>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Users table */}
      <Paper sx={{ overflow: 'auto' }}>
        <table className="tablekit-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead className="tablekit-thead">
            <tr>
              <th className="tablekit-th">שם משתמש</th>
              <th className="tablekit-th">טלפון</th>
              <th className="tablekit-th">מייל</th>
              <th className="tablekit-th">תפקידים</th>
              <th className="tablekit-th">ערים</th>
              <th className="tablekit-th">סטטוס</th>
              <th className="tablekit-th">נוצר</th>
              <th className="tablekit-th">פעולות</th>
            </tr>
          </thead>
          <tbody className="tablekit-tbody">
            {users.length === 0 && (
              <tr>
                <td className="tablekit-td" colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--ck-text-3, #999)' }}>
                  אין משתמשים במערכת
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u._id} className="tablekit-tr">
                <td className="tablekit-td" style={{ fontWeight: 600 }}>{u.username}</td>
                <td className="tablekit-td" style={{ direction: 'ltr', textAlign: 'right' }}>{u.phone || '—'}</td>
                <td className="tablekit-td" style={{ direction: 'ltr', textAlign: 'right' }}>{u.email || '—'}</td>
                <td className="tablekit-td">
                  {u.roles?.map((r) => (
                    <Chip key={r._id} label={r.name} size="small" sx={{ m: 0.3 }} color="primary" variant="outlined" />
                  ))}
                  {(!u.roles || u.roles.length === 0) && (
                    <Typography variant="caption" color="text.secondary">ללא תפקיד</Typography>
                  )}
                </td>
                <td className="tablekit-td">
                  {u.cities?.length > 0 ? (
                    u.cities.map((city) => (
                      <Chip key={city} label={city} size="small" sx={{ m: 0.3 }} variant="outlined" />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">כל הערים</Typography>
                  )}
                </td>
                <td className="tablekit-td">
                  <Chip
                    label={u.status === 'active' ? 'פעיל' : 'נעול'}
                    size="small"
                    color={u.status === 'active' ? 'success' : 'error'}
                    variant="filled"
                  />
                </td>
                <td className="tablekit-td">
                  {u.createdAt ? formatHebrewDate(u.createdAt) : '-'}
                </td>
                <td className="tablekit-td">
                  <Tooltip title="ערוך">
                    <IconButton size="small" onClick={() => openEditDialog(u)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="אפס סיסמה">
                    <IconButton size="small" onClick={() => openResetDialog(u)} color="warning">
                      <LockResetIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחק">
                    <IconButton size="small" onClick={() => handleDelete(u)} color="error">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'עריכת משתמש' : 'משתמש חדש'}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}

          <TextField
            label="שם משתמש"
            fullWidth
            margin="normal"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            autoFocus
          />

          <TextField
            label="טלפון (משמש גם לזיהוי)"
            fullWidth
            required
            margin="normal"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            helperText="המספר שממנו המשתמש מתקשר למערכת"
          />

          <TextField
            label="מייל"
            type="email"
            fullWidth
            margin="normal"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            inputProps={{ dir: 'ltr' }}
            helperText={editUser ? 'לשליחת פרטי כניסה' : 'סיסמה אקראית תיווצר אוטומטית — אחרי היצירה אפשר להעתיק או לשלוח במייל'}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>תפקידים</InputLabel>
            <Select
              multiple
              value={form.roles}
              onChange={(e) => setForm({ ...form, roles: e.target.value })}
              input={<OutlinedInput label="תפקידים" />}
              renderValue={(selected) =>
                selected
                  .map((id) => roles.find((r) => r._id === id)?.name || id)
                  .join(', ')
              }
            >
              {roles.map((role) => (
                <MenuItem key={role._id} value={role._id}>
                  <Checkbox checked={form.roles.includes(role._id)} />
                  <ListItemText primary={role.name} secondary={role.description} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>ערים (היקף הרשאה)</InputLabel>
            <Select
              multiple
              value={form.cities}
              onChange={(e) => setForm({ ...form, cities: e.target.value })}
              input={<OutlinedInput label="ערים (היקף הרשאה)" />}
              renderValue={(selected) =>
                selected.length ? selected.join(', ') : 'כל הערים'
              }
            >
              {cityOptions.map((city) => (
                <MenuItem key={city} value={city}>
                  <Checkbox checked={form.cities.includes(city)} />
                  <ListItemText primary={city} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              ריק = גישה לכל הערים. עם ערים נבחרות — המשתמש רואה רק נתונים מערים אלו (למעט מנהל).
            </Typography>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>סטטוס</InputLabel>
            <Select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              label="סטטוס"
            >
              <MenuItem value="active">פעיל</MenuItem>
              <MenuItem value="locked">נעול</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSave}>
            {editUser ? 'עדכן' : 'צור משתמש'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>איפוס סיסמה — {resetUsername}</DialogTitle>
        <DialogContent>
          <TextField
            label="סיסמה חדשה (רשות)"
            fullWidth
            margin="normal"
            type={showResetPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowResetPassword(!showResetPassword)} edge="end">
                    {showResetPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="ריק = סיסמה אקראית תיווצר אוטומטית. ידנית — לפחות 6 תווים"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>ביטול</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPassword}
            disabled={!!newPassword && newPassword.length < 6}
          >
            {newPassword ? 'אפס סיסמה' : 'צור סיסמה חדשה'}
          </Button>
        </DialogActions>
      </Dialog>

      <CredentialsDialog credentials={credentials} onClose={() => setCredentials(null)} onEmailSaved={loadData} />
    </Box>
  );
};

export default UsersManagement;
